import { Drawer, Group, Title, useMantineTheme } from '@mantine/core';
import classes from './SupplierActivitiesFilterDrawer.module.scss';
import { GoogleAutocompleteInput } from 'components/MainFilterContent/LocationFilterContent/GoogleAutocompleteInput';
import { ActivitySearchResult, Category } from 'types';
import { useSavedSearchValues } from 'context/SavedSearchFilterValuesContext';
import { PebbleButtonSet, PebbleRadio, PebbleRadioGroup } from 'components/ui';
import { SupplierSearchFilters } from 'components/SupplierDetails';
import { Dispatch, SetStateAction } from 'react';
import { filterOptionsWithAllDaysAndTime } from 'utils/filters/filters';
import { useMediaQuery } from '@mantine/hooks';
import AgeFilterContent from 'components/MainFilterContent/AgeFilterContent/AgeFilterContent';
import { PebbleButtonsEnum } from 'enums';
import { Actions, trackAction } from 'utils/amplitude';
import SupplierCategoryFilter from './SupplierCategoryFilter';

interface ISupplierActivitiesFilterDrawer {
  open: boolean;
  onClose(): void;
  activities: ActivitySearchResult[];
  searchFilters: SupplierSearchFilters;
  setFilterState: Dispatch<SetStateAction<SupplierSearchFilters>>;
  loading: boolean;
  supplierId: string;
  categories: Category[];
}

const SupplierActivitiesFilterDrawer: React.FC<ISupplierActivitiesFilterDrawer> = ({
  onClose,
  open,
  searchFilters,
  setFilterState,
  activities,
  loading,
  supplierId,
  categories,
}) => {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`, true);
  const { userDeniedGeolocation, setUserDeniedGeolocation } = useSavedSearchValues();
  const { distance } = filterOptionsWithAllDaysAndTime;
  return (
    <Drawer
      onClose={onClose}
      opened={open}
      position={isMobile ? 'bottom' : 'right'}
      size={isMobile ? 'xl' : 'md'}
      classNames={{
        content: classes.drawer,
        header: classes.drawerHeader,
        body: classes.drawerBody,
      }}
      title={
        <Title order={3} c={theme.colors.blue[8]}>
          Filter activities
        </Title>
      }
    >
      <Title order={4} c={theme.colors.blue[8]} mb="md" size="md">
        Location
      </Title>
      <GoogleAutocompleteInput
        autoLoadGeolocation={false}
        userDeniedGeolocation={userDeniedGeolocation}
        setUserDeniedGeolocation={setUserDeniedGeolocation}
        setLocationString={(val) => {
          setFilterState((prevState) => ({
            ...prevState,
            locationString: val,
          }));
        }}
        locationString={searchFilters.locationString}
        onSelect={(lat, lng) => {
          setFilterState((prevState) => ({
            ...prevState,
            coordinates: {
              lat,
              lng,
            },
            distance: 5,
          }));
        }}
        onClear={() => {
          setFilterState((prevState) => ({
            ...prevState,
            locationString: undefined,
            coordinates: null,
          }));
        }}
      />
      {searchFilters.coordinates && (
        <PebbleRadioGroup
          name="distance filter option"
          label={'Within'}
          onChange={(val) => {
            setFilterState((prevState) => ({
              ...prevState,
              distance: Number(val),
            }));
          }}
          value={searchFilters.distance?.toString()}
          apply_filter_styling={'true'}
          mb={'sm'}
        >
          <Group>
            {distance.map(({ label, value }) => (
              <PebbleRadio
                key={value}
                labelPosition="left"
                width={'100%'}
                value={value}
                label={label}
                pb={'sm'}
                py={isMobile ? 'xs' : 0}
                applySearchBarStyling={!isMobile}
                boldLabelFont={!isMobile}
                disabled={!searchFilters.coordinates}
              />
            ))}
          </Group>
        </PebbleRadioGroup>
      )}
      <AgeFilterContent
        selectedValues={{
          selectedAgeStart: searchFilters.ageStart,
          selectedAgeEnd: searchFilters.ageEnd,
        }}
        onChange={(val) => {
          setFilterState((prevState) => ({
            ...prevState,
            ageStart: val.selectedAgeStart,
            ageEnd: val.selectedAgeEnd,
          }));
        }}
        canReset={false}
      />
      <SupplierCategoryFilter
        categories={categories}
        selectedCategoryId={searchFilters.categoryId}
        onSelect={(val) => {
          setFilterState((prev) => ({
            ...prev,
            categoryId: val,
          }));
        }}
      />
      <Group justify={'space-between'} className={classes.footer}>
        <PebbleButtonSet
          btnVariant={PebbleButtonsEnum.TERTIARY}
          size="md"
          onClick={() => {
            setFilterState({
              coordinates: null,
              ageEnd: '',
              ageStart: '',
              distance: undefined,
              locationString: '',
            });
            onClose();
          }}
        >
          Clear all
        </PebbleButtonSet>
        <PebbleButtonSet
          btnVariant={PebbleButtonsEnum.PRIMARY}
          size="md"
          loading={loading}
          onClick={() => {
            onClose();
            trackAction(Actions.SUPPLIER_PAGE_FILTER_SHOW, { supplierId: supplierId });
          }}
        >
          Show {activities.length} activities
        </PebbleButtonSet>
      </Group>
    </Drawer>
  );
};

export default SupplierActivitiesFilterDrawer;
